import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  People as CustomersIcon,
  Inventory as ProductsIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  AccountCircle as ProfileIcon,
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationDropdown from '../../components/NotificationDropdown';

interface MenuItemType {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
}

const menuItems: MenuItemType[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    label: 'Yönetici Dashboard',
    path: '/manager-dashboard',
    icon: <DashboardIcon />,
    roles: ['manager', 'admin'],
  },
  {
    label: 'Siparişler',
    path: '/orders',
    icon: <OrdersIcon />,
  },
  {
    label: 'Siparişlerim',
    path: '/my-orders',
    icon: <OrdersIcon />,
    roles: ['salesperson'],
  },
  {
    label: 'Yeni Sipariş',
    path: '/create-order',
    icon: <OrdersIcon />,
    roles: ['salesperson'],
  },
  {
    label: 'Envanter',
    path: '/inventory',
    icon: <ProductsIcon />,
    roles: ['warehouse', 'admin'],
  },
  {
    label: 'Müşteriler',
    path: '/customers',
    icon: <CustomersIcon />,
  },
  {
    label: 'Faturalar',
    path: '/invoices',
    icon: <ReportsIcon />,
  },
  {
    label: 'Bildirimler',
    path: '/notifications',
    icon: <SettingsIcon />,
  },
  {
    label: 'Yönetim Paneli',
    path: '/admin',
    icon: <SettingsIcon />,
    roles: ['admin'],
  },
  {
    label: 'Tenant Yönetimi',
    path: '/tenant-management',
    icon: <BusinessIcon />,
    roles: ['admin'],
  },
  {
    label: 'Ayarlar',
    path: '/settings',
    icon: <SettingsIcon />,
  },
  {
    label: 'Ürün Yönetimi',
    path: '/product-management',
    icon: <InventoryIcon />,
    roles: ['admin'],
  },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
}

export function DashboardLayout({ children, title, breadcrumbs }: DashboardLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  // const [tenantMenuAnchor, setTenantMenuAnchor] = useState<null | HTMLElement>(null); // Removed for security
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, logout } = useAuth();

  // Available tenants for switching
  const availableTenants = [
    {
      id: "default",
      name: "OrderApp Teknoloji A.Ş.",
      subdomain: "default",
      color: "#1976d2",
    },
    {
      id: "demo",
      name: "Demo Şirketi A.Ş.",
      subdomain: "demo",
      color: "#1976d2",
    },
    {
      id: "client1",
      name: "Client 1 Teknoloji Ltd. Şti.",
      subdomain: "client1",
      color: "#2e7d32",
    },
  ];

  const currentTenant = availableTenants.find(t => t.subdomain === tenant?.subdomain) || availableTenants[0];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  // Tenant switcher functions removed for security
  // const handleTenantMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
  //   setTenantMenuAnchor(event.currentTarget);
  // };
  // const handleTenantMenuClose = () => {
  //   setTenantMenuAnchor(null);
  // };
  // const handleTenantSwitch = (subdomain: string) => {
  //   // Redirect to the appropriate subdomain
  //   if (subdomain === "default") {
  //     window.location.href = "https://ordertrackingapp.com";
  //   } else {
  //     window.location.href = `https://${subdomain}.ordertrackingapp.com`;
  //   }
  //   handleTenantMenuClose();
  // };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  const drawerWidth = sidebarCollapsed ? 80 : 240;

  const drawer = (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {!sidebarCollapsed && (
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <DashboardIcon />
          </Avatar>
        )}
        {!sidebarCollapsed && (
          <Box>
            <Typography variant="h6" noWrap component="div">
              OrderApp
            </Typography>
            {tenant && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {tenant.company_name}
              </Typography>
            )}
          </Box>
        )}
        <IconButton
          onClick={handleSidebarToggle}
          sx={{ ml: sidebarCollapsed ? 0 : 'auto' }}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  px: sidebarCollapsed ? 2 : 3,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? 'primary.contrastText' : 'inherit',
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText primary={item.label} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          zIndex: 1400,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            {title && (
              <Typography variant="h6" noWrap component="div">
                {title}
              </Typography>
            )}
            {tenant && (
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                {tenant.company_name}
              </Typography>
            )}
            {breadcrumbs && (
              <Breadcrumbs
                separator={<ChevronRightIcon fontSize="small" />}
                sx={{ color: 'inherit', opacity: 0.8 }}
              >
                <Link
                  color="inherit"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/dashboard');
                  }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Ana Sayfa
                </Link>
                {breadcrumbs.map((crumb, index) => (
                  <Typography key={index} color="inherit" variant="body2">
                    {crumb.label}
                  </Typography>
                ))}
              </Breadcrumbs>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationDropdown />
            
            {/* Company Display (Non-clickable) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <Avatar sx={{ width: 24, height: 24, bgcolor: currentTenant.color }}>
                <BusinessIcon fontSize="small" />
              </Avatar>
              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                {currentTenant.name}
              </Typography>
            </Box>
            
            <IconButton
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{
                zIndex: 1600,
              }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.full_name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            zIndex: isMobile ? 1500 : 1200, // Mobile'da daha yüksek zIndex
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
          '& .MuiBackdrop-root': {
            zIndex: 1400, // Backdrop zIndex'i artırıldı
          },
          zIndex: isMobile ? 1500 : 'auto',
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { 
            xs: '100%', // Mobile'da tam genişlik
            md: `calc(100% - ${drawerWidth}px)` 
          },
          ml: { 
            xs: 0, // Mobile'da margin yok
            md: `${drawerWidth}px` 
          },
          mt: '64px', // AppBar height
          zIndex: 0,
          position: 'relative',
          backgroundColor: 'background.default',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleNavigation('/profile')}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          Profil
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Çıkış Yap
        </MenuItem>
      </Menu>

      {/* Tenant Switcher Menu - Removed for security */}
    </Box>
  );
} 